import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText, CheckCircle2, Clock, AlertTriangle, PenTool, Shield } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { SignaturePad } from "@/components/contracts/SignaturePad";

interface ContractContent {
  title?: string;
  executiveSummary?: string;
  scopeOfWork?: string;
  deliverables?: string;
  timeline?: string;
  pricing?: string;
  termsAndConditions?: string;
  paymentTerms?: string;
}

interface Contract {
  id: string;
  title: string;
  status: string;
  signer_name: string | null;
  signer_email: string | null;
  expires_at: string | null;
  sent_at: string | null;
  signed_at: string | null;
  metadata: { content?: ContractContent } | null;
  deals?: { title: string; amount: number | null; accounts?: { name: string } | null } | null;
}

export default function PublicContractSign() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [contract, setContract] = useState<Contract | null>(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [signatureData, setSignatureData] = useState("");

  useEffect(() => {
    if (token) {
      fetchContract();
    }
  }, [token]);

  const fetchContract = async () => {
    try {
      const { data, error } = await supabase
        .from("contracts")
        .select(`
          id, title, status, signer_name, signer_email, expires_at, sent_at, signed_at, metadata,
          deals(title, amount, accounts(name))
        `)
        .eq("signing_token", token)
        .single();

      if (error) throw error;
      setContract(data as unknown as Contract);
      if (data.signer_name) setSignerName(data.signer_name);
      if (data.status === "signed") setSigned(true);

      // Mark as viewed
      if (data.status === "sent") {
        await supabase
          .from("contracts")
          .update({ viewed_at: new Date().toISOString() })
          .eq("signing_token", token);
      }
    } catch (error) {
      console.error("Error fetching contract:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async () => {
    if (!contract || !agreed || !signerName || !signatureData) return;

    setSigning(true);
    try {
      const { error } = await supabase
        .from("contracts")
        .update({
          status: "signed",
          signed_at: new Date().toISOString(),
          signer_name: signerName,
          signature_url: signatureData,
        })
        .eq("signing_token", token);

      if (error) throw error;

      setSigned(true);
      toast.success("Contract signed successfully!");
    } catch (error: any) {
      toast.error(error.message || "Failed to sign contract");
    } finally {
      setSigning(false);
    }
  };

  const content = contract?.metadata?.content as ContractContent | undefined;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading contract...</div>
      </div>
    );
  }

  if (!contract || !token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md mx-auto">
          <CardContent className="flex flex-col items-center py-12">
            <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
            <h2 className="text-xl font-semibold mb-2">Contract Not Found</h2>
            <p className="text-muted-foreground text-center">
              This contract link is invalid or has expired.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (signed || contract.status === "signed") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md mx-auto">
          <CardContent className="flex flex-col items-center py-12">
            <CheckCircle2 className="h-16 w-16 text-green-500 mb-4" />
            <h2 className="text-2xl font-semibold mb-2">Contract Signed</h2>
            <p className="text-muted-foreground text-center mb-4">
              Thank you! This contract has been successfully signed.
            </p>
            {contract.signed_at && (
              <p className="text-sm text-muted-foreground">
                Signed on {format(new Date(contract.signed_at), "PPP 'at' p")}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const isExpired = contract.expires_at && new Date(contract.expires_at) < new Date();

  if (isExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md mx-auto">
          <CardContent className="flex flex-col items-center py-12">
            <Clock className="h-16 w-16 text-amber-500 mb-4" />
            <h2 className="text-2xl font-semibold mb-2">Contract Expired</h2>
            <p className="text-muted-foreground text-center">
              This contract has expired and can no longer be signed. Please contact the sender for a new contract.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-primary" />
              <div>
                <CardTitle className="text-2xl">{contract.title}</CardTitle>
                <CardDescription>
                  {contract.deals?.accounts?.name && `From ${contract.deals.accounts.name}`}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4 text-sm">
              <Badge variant="outline">
                <Clock className="h-3 w-3 mr-1" />
                Sent {contract.sent_at ? format(new Date(contract.sent_at), "PPP") : "N/A"}
              </Badge>
              {contract.expires_at && (
                <Badge variant="outline" className="text-amber-600">
                  Expires {format(new Date(contract.expires_at), "PPP")}
                </Badge>
              )}
              {contract.deals?.amount && (
                <Badge variant="secondary">
                  Value: ${contract.deals.amount.toLocaleString()}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Contract Content */}
        <Card>
          <CardHeader>
            <CardTitle>Contract Terms</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {content?.executiveSummary && (
              <div>
                <h3 className="font-semibold mb-2">Executive Summary</h3>
                <p className="text-muted-foreground whitespace-pre-wrap">{content.executiveSummary}</p>
              </div>
            )}
            
            {content?.scopeOfWork && (
              <div>
                <h3 className="font-semibold mb-2">Scope of Work</h3>
                <p className="text-muted-foreground whitespace-pre-wrap">{content.scopeOfWork}</p>
              </div>
            )}
            
            {content?.deliverables && (
              <div>
                <h3 className="font-semibold mb-2">Deliverables</h3>
                <p className="text-muted-foreground whitespace-pre-wrap">{content.deliverables}</p>
              </div>
            )}
            
            {content?.timeline && (
              <div>
                <h3 className="font-semibold mb-2">Timeline</h3>
                <p className="text-muted-foreground whitespace-pre-wrap">{content.timeline}</p>
              </div>
            )}
            
            {content?.pricing && (
              <div>
                <h3 className="font-semibold mb-2">Pricing</h3>
                <p className="text-muted-foreground whitespace-pre-wrap">{content.pricing}</p>
              </div>
            )}
            
            {content?.paymentTerms && (
              <div>
                <h3 className="font-semibold mb-2">Payment Terms</h3>
                <p className="text-muted-foreground whitespace-pre-wrap">{content.paymentTerms}</p>
              </div>
            )}
            
            {content?.termsAndConditions && (
              <div>
                <h3 className="font-semibold mb-2">Terms & Conditions</h3>
                <p className="text-muted-foreground whitespace-pre-wrap">{content.termsAndConditions}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Signature Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PenTool className="h-5 w-5" />
              Sign Contract
            </CardTitle>
            <CardDescription>
              Please review the contract above and sign below to accept the terms
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="signer-name">Your Full Name *</Label>
              <Input
                id="signer-name"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Enter your full legal name"
                className="text-lg"
              />
            </div>

            <div className="space-y-2">
              <Label>Your Signature *</Label>
              <SignaturePad
                signerName={signerName}
                onSignatureChange={setSignatureData}
              />
            </div>

            <Separator />

            <div className="flex items-start space-x-3 p-4 bg-muted/50 rounded-lg">
              <Checkbox
                id="agree"
                checked={agreed}
                onCheckedChange={(checked) => setAgreed(checked as boolean)}
                className="mt-0.5"
              />
              <label
                htmlFor="agree"
                className="text-sm leading-relaxed cursor-pointer"
              >
                I have read and agree to the terms and conditions outlined in this contract. 
                I understand that by clicking "Sign Contract" below, I am providing my electronic 
                signature which is legally binding under applicable laws.
              </label>
            </div>

            <Button
              className="w-full"
              size="lg"
              onClick={handleSign}
              disabled={!agreed || !signerName || !signatureData || signing}
            >
              {signing ? "Signing..." : "Sign Contract"}
            </Button>

            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <Shield className="h-3.5 w-3.5" />
              <span>Your signature is encrypted and legally binding</span>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground">
          Powered by Aiveliq360 • Secure Electronic Signatures
        </p>
      </div>
    </div>
  );
}
